<template>
    <v-container fluid>
        <v-card>
          <v-toolbar color="primary" flat dark dense>
            <v-toolbar-title class="ml-2">Dealer Details</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-form ref="form" lazy-validation>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="dealer.name" label="Dealer Name"></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field v-model="dealer.dms_key" readonly label="Dms Key"></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field v-model="dealer.short_code" label="Short Code"></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-btn class="mt-2" color="primary" @click="updateDealer">Update Dealer</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card class="ma-0 pa-0">
            <v-toolbar color="secondary" flat dark dense>
              <v-toolbar-title class="ml-2">Dealer Rooftops</v-toolbar-title>
              <v-btn small class="ml-auto mr-2" color="primary" @click="addRooftop">Create New Rooftop</v-btn>
            </v-toolbar>
              <v-row class="pa-2" justify="space-between">
                <v-col cols="2">
                  <v-treeview
                    :items="items"
                    :active.sync="active"
                    :open.sync="open"
                    activatable
                    color="warning"
                    open-on-click
                    :multiple-active="false"
                    transition
                    dense>
                      <template v-slot:prepend="{ item }">
                          <v-icon v-if="!item.children">
                            {{ item.icon }}
                          </v-icon>
                      </template>
                  </v-treeview>
                </v-col>

                <v-divider vertical></v-divider>

                <v-col cols="10">
                    <v-scroll-y-transition mode="out-in">
                        <div v-if="!selectedRooftop" class="text-h6 grey--text text--lighten-1 font-weight-light"
                            style="align-self: center;">
                            Select a rooftop to view details
                        </div>
                        <v-card v-else :key="selectedRooftop.id" class="pt-6 mx-auto" flat>
                          <!-- TODO - turn this into v-model over rooftop-->
                          <component
                            :is="lookupRooftopSegment(selectedRooftopSegment, 'componentName')"
                            v-model="selectedRooftop"
                            :rooftop="selectedRooftop"
                            :dealer="dealer"
                          />
                          <v-card-actions v-show="lookupRooftopSegment(selectedRooftopSegment, 'showSave')">
                            <v-col class="text-center">
                              <v-btn color="primary" type="submit" @click.prevent="saveRooftop">Save</v-btn>
                              <v-btn :to="{ name: 'dealers_path' }" class="ml-2">Cancel</v-btn>
                            </v-col>
                          </v-card-actions>
                        </v-card>
                    </v-scroll-y-transition>
                </v-col>
              </v-row>
          </v-card>
        </v-card>
    </v-container>
</template>

<script>
import { DealerApi, DealerRooftopApi } from '@/services'
import { nextTick } from 'process'
import { StatesForSelect } from '@/utils/us_states'
import Map from '@/components/map/Map'
import validationMixin from '@/validators/validations_mixin'
import DealerCreateCard from './DealerCreateCard'
import { RooftopUrls, RooftopContacts, RooftopStoreHours, RooftopPreviewData, RooftopDetail } from './rooftops/index'

const RooftopChildrenData = [
  { name: 'Rooftop Details', key: 'detail', icon: 'mdi-store', componentName: RooftopDetail.name, showSave: true },
  { name: 'Urls', key: 'urls', icon: 'mdi-link', componentName: RooftopUrls.name, showSave: true },
  { name: 'Contacts', key: 'contacts', icon: 'mdi-account-group', componentName: RooftopContacts.name, showSave: false },
  { name: 'Store Hours', key: 'store_hours', icon: 'mdi-clock', componentName: RooftopStoreHours.name, showSave: true },
  { name: 'Preview Data', key: 'preview_data', icon: 'mdi-binoculars', componentName: RooftopPreviewData.name, showSave: true }
]

export default {
  name: 'dealer',
  mixins: [validationMixin],
  components: {
    [DealerCreateCard.name]: DealerCreateCard,
    [RooftopDetail.name]: RooftopDetail,
    [RooftopUrls.name]: RooftopUrls,
    [RooftopContacts.name]: RooftopContacts,
    [RooftopStoreHours.name]: RooftopStoreHours,
    [RooftopPreviewData.name]: RooftopPreviewData,
    [Map.name]: Map
  },
  props: {
    dealerId: {
      type: [Number, String],
      required: true
    }
  },
  data: () => ({
    active: [],
    open: [],
    dealer: {},
    selectedRooftop: undefined,
    selectedRooftopSegment: 'detail',
    states: StatesForSelect
  }),
  computed: {
    rooftops () {
      return (this.dealer.rooftops || []).sort((a, b) => {
        if (a.default_rooftop) return -1
        return a.created_at < b.created_at ? -1 : 1
      })
    },
    items () {
      return this.rooftops.map(rooftop => {
        return {
          id: rooftop.id,
          name: rooftop.default_rooftop ? 'Dealer Default' : rooftop.name,
          children: RooftopChildrenData.map(rooftopDetail => {
            return { id: `${rooftop.id || 0}:${rooftopDetail.key}`, ...rooftopDetail }
          })
        }
      })
    }
  },
  watch: {
    active (newVal) {
      if (!this.active.length) {
        this.selectedRooftop = undefined
        this.selectedRooftopSegment = 'detail'
      } else {
        const [ _id, segment ] = this.active[0].split(':')
        const id = parseInt(_id)
        this.selectedRooftop = this.rooftops.find(rooftop => rooftop.id === id)
        this.selectedRooftopSegment = segment
      }
    },
    dealerId: {
      immediate: true,
      handler (id) {
        if (!id) return
        this.loadDealer(id)
          .then(() => {
            nextTick(() => {
              this.setTreeActiveOpenState(`${this.rooftops[0].id}:detail`)
            })
          }).catch(err => {
            this.$logger.error(err.message)
            this.$flash({
              showClose: true,
              message: `Failed to load Dealer. ${err.message}`,
              type: 'error',
              duration: 0
            })
          })
      }
    }
  },
  methods: {
    async loadDealer (dealerId) {
      const resp = await DealerApi.getDealer({ id: dealerId })
      this.dealer = resp.data
      return this.dealer
    },
    async updateDealer () {
      const d = (await DealerApi.updateDealer(this.dealer)).data
      this.dealer = { ...this.dealer, ...d }
      this.dealer.rooftops = []
      this.dealer.rooftops = d.rooftops
      this.$flash({
        showClose: true,
        message: 'Dealer updated successfully',
        type: 'mini',
        duration: 2000
      })
    },
    lookupRooftopSegment (rooftopComponentKey, propName) {
      return RooftopChildrenData.find(rooftopComponent => rooftopComponent.key === rooftopComponentKey)[propName]
    },
    onRooftopChange (rooftop) {
      this.$set(this.selectedRooftop, { ...this.selectedRooftop, ...rooftop })
    },
    async saveRooftop () {
      try {
        const isNew = this.selectedRooftop.id === 0
        const resp = await DealerRooftopApi.saveRooftop(this.dealerId, this.selectedRooftop)
        const savedRooftop = resp.data
        let rooftopIdx = -1
        if (isNew) {
          rooftopIdx = this.dealer.rooftops.findIndex(rooftop => rooftop.id === 0)
        } else {
          rooftopIdx = this.dealer.rooftops.findIndex(rooftop => rooftop.id === savedRooftop.id)
        }

        if (rooftopIdx > -1) {
          this.dealer.rooftops.splice(rooftopIdx, 1, savedRooftop)
        } else {
          this.dealer.rooftops.push(savedRooftop)
        }
        this.selectedRooftop = savedRooftop

        await this.loadDealer(this.dealerId)
        nextTick(() => {
          if (isNew) {
            this.setTreeActiveOpenState(`${savedRooftop.id}:detail`)
          }
        })
        this.$flash({
          showClose: true,
          message: 'Rooftop saved successfully',
          type: 'mini',
          duration: 2000
        })
      } catch (error) {
        this.$logger.error(error.message)
        this.$flash({
          showClose: true,
          message: `Failed to save Rooftop. ${error.message}`,
          type: 'error',
          duration: 0
        })
      }
    },
    setTreeActiveOpenState (tabKey) {
      // eslint-disable-next-line no-unused-vars
      const [rooftopId, segment] = tabKey.split(':')
      const rooftopMatchId = parseInt(rooftopId)
      if (!this.open.find(openKey => openKey === rooftopMatchId)) {
        this.$logger.trace('adding to open=', rooftopMatchId)
        this.open.push(rooftopMatchId)
      }
      // set active tab
      this.active = [tabKey]
    },
    addRooftop () {
      const rooftop = {
        id: 0,
        name: '',
        time_zone: 'Central Time (US & Canada)',
        default_rooftop: false,
        address_line_1: this.dealer.address_line_1,
        address_line_2: this.dealer.address_line_2,
        city: this.dealer.city,
        state: this.dealer.state,
        postal_code: this.dealer.postal_code,
        urls: {},
        store_hours: {},
        preview_data: {},
        created_at: new Date()
      }
      this.dealer.rooftops.push(rooftop)
      nextTick(() => {
        this.setTreeActiveOpenState('0:detail')
      })
    }
  }
}
</script>
